<script lang="jsx">
import { $on, $off, $once, $emit } from '../../utils/gogocodeTransfer'
import * as Vue from 'vue'
import events from './events'
import { i18nRender } from '@/locales'

// 首页
const homeRoute = {
  fullPath: '/index',
  path: '/index',
  meta: {
    title: '首页',
  },
}
export default {
  name: 'MultiTab',
  data() {
    return {
      activeIndex: 0,
      isInitialized: false, // 标记是否已初始化
    }
  },
  computed: {
    // 从 store 读取 pages，确保响应式
    pages() {
      return this.$store.state.app.pages || []
    },
    // 使用 computed 确保响应式更新
    pagesList() {
      // 每次访问都会重新计算，确保响应式
      return Array.isArray(this.pages) ? this.pages : []
    },
  },
  created() {
    // bind event
    $on(
      $on(
        $on(events, 'open', (val) => {
          if (!val) {
            throw new Error(`multi-tab: open tab ${val} err`)
          }
          this.activeIndex = val
        }),
        'close',
        (val) => {
          if (!val) {
            this.closeThat(this.activeIndex)
            return
          }
          this.closeThat(val)
        }
      ),
      'rename',
      ({ key, name }) => {
        try {
          const item = this.pages.find((item) => item.path === key)
          if (item) {
            item.meta.customTitle = name
            // 触发响应式更新 - 更新 store
            this.$store.commit('SET_MULTITAB_PAGES', [...this.pages])
          }
        } catch (e) {}
      }
    )
    
    // 初始化：处理当前路由
    if (this.$route) {
      // 如果当前路由不是首页，先添加首页
      if (this.$route.path !== homeRoute.path) {
        const homeExists = this.pages.some(item => item.path === homeRoute.path)
        if (!homeExists) {
          this.$store.commit('ADD_MULTITAB_PAGE', homeRoute)
          console.log('[MultiTab] created: 添加首页')
        }
      }
      
      // 处理当前路由
      this.handleRouteChange(this.$route, null)
    }
    
    console.log('[MultiTab] created 完成，pages 数量:', this.pages.length)
    
    // 使用 $watch 手动监听路由变化，确保能正确触发
    const unwatch = this.$watch(
      '$route',
      (newVal, oldVal) => {
        console.log('[MultiTab] ⚡ $watch $route 触发:', {
          from: oldVal?.path,
          to: newVal.path,
          oldValExists: !!oldVal,
          newValExists: !!newVal,
          currentPagesCount: this.pages.length
        })
        this.handleRouteChange(newVal, oldVal)
      },
      {
        immediate: false, // 不在初始化时触发，因为 created 中已经处理了
        deep: true
      }
    )
    
    // 保存 unwatch 函数，以便在组件销毁时取消监听
    this._routeUnwatch = unwatch
    console.log('[MultiTab] ✅ 路由监听已设置')
    
    // 同时监听全局事件，作为备用方案
    this._routeChangeHandler = (event) => {
      const { to, from } = event.detail
      console.log('[MultiTab] 📡 收到全局路由变化事件:', {
        from: from?.path,
        to: to.path
      })
      this.handleRouteChange(to, from)
    }
    window.addEventListener('route-changed', this._routeChangeHandler)
    console.log('[MultiTab] ✅ 全局事件监听已设置')
  },
  mounted() {
    // mounted 时确保当前路由已添加（如果 created 中未处理）
    this.$nextTick(() => {
      if (this.$route) {
        this.handleRouteChange(this.$route, null)
      }
    })
    console.log('[MultiTab] ✅ mounted 完成')
  },
  beforeUnmount() {
    // 清理路由监听
    if (this._routeUnwatch) {
      this._routeUnwatch()
      console.log('[MultiTab] ✅ 路由监听已清理')
    }
    // 清理全局事件监听
    if (this._routeChangeHandler) {
      window.removeEventListener('route-changed', this._routeChangeHandler)
      console.log('[MultiTab] ✅ 全局事件监听已清理')
    }
  },
  methods: {
    onEdit(targetKey, action) {
      console.log('[MultiTab] onEdit 被调用:', { targetKey, action, targetKeyType: typeof targetKey })
      // targetKey 现在是字符串格式的索引（如 "0", "1"），直接转换为数字
      let targetIndex = -1
      if (typeof targetKey === 'string') {
        // 直接解析字符串为数字
        targetIndex = parseInt(targetKey, 10)
        if (isNaN(targetIndex)) {
          console.warn('[MultiTab] onEdit: 无法解析 targetKey', targetKey)
          return
        }
      } else if (typeof targetKey === 'number') {
        targetIndex = targetKey
      } else {
        console.warn('[MultiTab] onEdit: 未知的 targetKey 类型', targetKey)
        return
      }
      
      console.log('[MultiTab] onEdit: 解析后的索引', targetIndex, '当前 activeIndex:', this.activeIndex)
      
      if (action === 'remove' && targetIndex >= 0 && targetIndex < this.pages.length) {
        this.remove(targetIndex)
      } else {
        console.warn('[MultiTab] onEdit: 无效的删除操作', { targetIndex, action, pagesLength: this.pages.length })
      }
    },
    remove(targetIndex) {
      console.log('[MultiTab] remove 被调用:', { 
        targetIndex, 
        '当前 pages 数量': this.pages.length,
        '当前 activeIndex': this.activeIndex,
        '要删除的页面': this.pages[targetIndex]?.path
      })
      
      if (targetIndex < 0 || targetIndex >= this.pages.length) {
        console.warn('[MultiTab] remove: 无效的索引', targetIndex)
        return
      }
      
      // 如果只剩一个标签，不允许删除
      if (this.pages.length <= 1) {
        this.$message.info('这是最后一个标签了, 无法被关闭')
        return
      }
      
      const pageToRemove = this.pages[targetIndex]
      const isRemovingActiveTab = this.activeIndex === targetIndex
      
      // 先删除页面
      this.$store.dispatch('tagsView/delCachedView', pageToRemove)
      this.$store.commit('REMOVE_MULTITAB_PAGE', targetIndex)
      
      // 判断当前标签是否关闭，若关闭则跳转到上一个或下一个标签页
      if (isRemovingActiveTab) {
        // 如果删除的是当前激活的标签
        let newIndex = -1
        if (targetIndex > 0) {
          // 如果删除的不是第一个，跳转到上一个标签
          newIndex = targetIndex - 1
        } else {
          // 如果删除的是第一个，跳转到新的第一个标签（原来的第二个）
          newIndex = 0
        }
        
        // 确保新索引有效
        if (newIndex >= 0 && newIndex < this.pages.length) {
          this.activeIndex = newIndex
          const page = this.pages[newIndex]
          console.log('[MultiTab] 删除当前标签后跳转到:', { newIndex, path: page.path })
          
          if (page.name) {
            this.$store.dispatch('tagsView/addCachedView', page)
          }
          // 跳转到新的标签页
          this.$router.push({
            path: page.path,
            query: page.query || {},
            params: page.params || {},
          }).catch(err => {
            if (err.name !== 'NavigationDuplicated') {
              console.error('MultiTab: 路由跳转失败', err)
            }
          })
        }
      } else if (this.activeIndex > targetIndex) {
        // 如果删除的标签在当前激活标签之前，需要调整 activeIndex
        this.activeIndex--
        console.log('[MultiTab] 调整 activeIndex:', this.activeIndex)
      }
      
      // 强制更新视图
      this.$forceUpdate()
      console.log('[MultiTab] remove 完成，新的 pages 数量:', this.pages.length, '新的 activeIndex:', this.activeIndex)
    },
    selectedLastPath() {
      if (this.pages.length === 0) {
        return
      }
      this.activeIndex = this.pages.length - 1
      const page = this.pages[this.activeIndex]
      if (page.name) {
        this.$store.dispatch('tagsView/addCachedView', page)
      }
      this.$router.push({
        path: page.path,
        query: page.query || {},
        params: page.params || {},
      }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error('MultiTab: 路由跳转失败', err)
        }
      })
    },

    // content menu
    closeThat(e) {
      // 判断是否为最后一个标签页，如果是最后一个，则无法被关闭
      if (this.pages.length > 1) {
        this.remove(e)
      } else {
        this.$message.info('这是最后一个标签了, 无法被关闭')
      }
    },
    closeLeft(e) {
      if (e > 1) {
        for (let index = 1; index < e; index++) {
          this.remove(1)
        }
      } else {
        this.$message.info('左侧没有标签')
      }
    },
    closeRight(e) {
      if (e < this.pages.length - 1) {
        while (e < this.pages.length - 1) {
          this.remove(e + 1)
        }
      } else {
        this.$message.info('右侧没有标签')
      }
    },
    closeAll(e) {
      while (this.pages.length > 1) {
        this.remove(1)
      }
    },
    closeMenuClick(key, route) {
      this[key](route)
    },
    renderTabPaneMenu(e, index) {
      return (
        <a-menu
          {...{
            on: {
              click: ({ key, item, domEvent }) => {
                this.closeMenuClick(key, index)
              },
            },
          }}
        >
          <a-menu-item disabled={index === 0} key="closeThat">
            关闭当前标签
          </a-menu-item>
          <a-menu-item
            disabled={this.pages.length === index + 1}
            key="closeRight"
          >
            关闭右侧
          </a-menu-item>
          <a-menu-item disabled={index <= 1} key="closeLeft">
            关闭左侧
          </a-menu-item>
          <a-menu-item disabled={this.pages.length === 1} key="closeAll">
            关闭全部
          </a-menu-item>
        </a-menu>
      )
    },
    // render
    renderTabPane(title, keyPath, index) {
      const menu = this.renderTabPaneMenu(keyPath, index)
      const isActive = index === this.activeIndex

      return (
        <a-dropdown overlay={menu} trigger={['contextmenu']}>
          <span 
            style={{ 
              userSelect: 'none',
              color: isActive ? '#165DFF' : 'inherit'
            }}
          >
            {i18nRender(title)}
          </span>
        </a-dropdown>
      )
    },
    // 处理路由变化的核心方法
    handleRouteChange(newVal, oldVal) {
      console.log('[MultiTab] 🔄 路由变化触发:', {
        from: oldVal?.path,
        to: newVal.path,
        title: newVal.meta?.title,
        name: newVal.name,
        currentPagesCount: this.pages.length,
        isInitialized: this.isInitialized
      })
      
      // 确保路由对象完整，包含所有必要信息
      const routeToAdd = {
        ...newVal,
        fullPath: newVal.fullPath || newVal.path,
        path: newVal.path,
        name: newVal.name,
        meta: { ...(newVal.meta || {}) },
        query: { ...(newVal.query || {}) },
        params: { ...(newVal.params || {}) },
      }
      
      // 确保 meta.title 存在
      if (!routeToAdd.meta.title) {
        if (routeToAdd.name) {
          routeToAdd.meta.title = routeToAdd.name
        } else if (routeToAdd.path) {
          // 从路径中提取标题
          const pathParts = routeToAdd.path.split('/').filter(p => p)
          routeToAdd.meta.title = pathParts[pathParts.length - 1] || '未命名页面'
        } else {
          routeToAdd.meta.title = '未命名页面'
        }
      }
      
      // 优先使用 fullPath 来判断，因为 fullPath 包含完整的路径和查询参数
      // 简化判断逻辑：只要 fullPath 不同，就认为是不同的页面
      let currentIndex = this.pages.findIndex((item) => {
        // 如果 fullPath 相同，则认为是同一个页面
        if (item.fullPath && routeToAdd.fullPath && item.fullPath === routeToAdd.fullPath) {
          console.log('[MultiTab] 找到相同页面 (fullPath):', {
            itemFullPath: item.fullPath,
            routeFullPath: routeToAdd.fullPath
          })
          return true
        }
        // 如果 fullPath 不同，但 path 相同且 params 相同，也认为是同一个页面
        if (item.path === routeToAdd.path) {
          const itemParams = JSON.stringify(item.params || {})
          const newParams = JSON.stringify(routeToAdd.params || {})
          if (itemParams === newParams) {
            console.log('[MultiTab] 找到相同页面 (path + params):', {
              itemPath: item.path,
              routePath: routeToAdd.path,
              itemParams,
              newParams
            })
            return true
          }
        }
        return false
      })
      
      console.log('[MultiTab] 查找页面结果:', {
        currentIndex,
        routePath: routeToAdd.path,
        routeFullPath: routeToAdd.fullPath,
        existingPages: this.pages.map(p => ({ path: p.path, fullPath: p.fullPath }))
      })
      
      if (currentIndex < 0) {
        // 如果不存在，添加新页面 - 使用 store mutation
        this.$store.commit('ADD_MULTITAB_PAGE', routeToAdd)
        currentIndex = this.pages.length - 1
        console.log('[MultiTab] ✅ 添加新页面:', {
          path: routeToAdd.path,
          title: routeToAdd.meta?.title,
          name: routeToAdd.name,
          index: currentIndex,
          totalPages: this.pages.length,
          pagesArray: this.pages.map(p => p.path)
        })
      } else {
        // 如果已存在，更新页面信息（保持访问顺序，不移动位置）
        const newPages = [...this.pages]
        newPages[currentIndex] = { ...newPages[currentIndex], ...routeToAdd }
        this.$store.commit('SET_MULTITAB_PAGES', newPages)
        console.log('[MultiTab] 🔄 更新已存在页面:', {
          path: routeToAdd.path,
          title: routeToAdd.meta?.title,
          index: currentIndex
        })
      }
      
      // 更新 activeIndex - 确保当前路由对应的tab被高亮
      this.activeIndex = currentIndex
      
      // 确保 activeIndex 正确同步（使用 $nextTick 确保 store 更新完成）
      this.$nextTick(() => {
        // 再次查找当前路由对应的索引，确保同步
        const syncIndex = this.pages.findIndex((item) => {
          if (item.fullPath && routeToAdd.fullPath && item.fullPath === routeToAdd.fullPath) {
            return true
          }
          if (item.path === routeToAdd.path) {
            const itemParams = JSON.stringify(item.params || {})
            const newParams = JSON.stringify(routeToAdd.params || {})
            return itemParams === newParams
          }
          return false
        })
        if (syncIndex >= 0 && syncIndex !== this.activeIndex) {
          console.log('[MultiTab] 同步 activeIndex:', { from: this.activeIndex, to: syncIndex })
          this.activeIndex = syncIndex
        }
      })
      
      // 确保缓存视图被添加
      if (routeToAdd.name) {
        this.$store.dispatch('tagsView/addCachedView', routeToAdd)
      }
      
      // 标记已初始化
      if (!this.isInitialized) {
        this.isInitialized = true
      }
      
      // 立即强制更新视图 - 不使用 $nextTick，确保立即更新
      this.$forceUpdate()
      console.log('[MultiTab] ✅ 视图已更新，当前页面数:', this.pages.length)
      console.log('[MultiTab] 📋 页面列表:', this.pages.map((p, i) => ({
        index: i,
        path: p.path,
        title: p.meta?.title || p.meta?.customTitle || p.name,
        isActive: i === this.activeIndex
      })))
    },
  },
  watch: {
    activeIndex: function (index, oldIndex) {
      // 避免在初始化时触发
      if (oldIndex === undefined || index === oldIndex) {
        return
      }
      
      if (!this.pages || this.pages.length === 0 || index < 0 || index >= this.pages.length) {
        console.warn('[MultiTab] activeIndex watch: 无效的索引', index)
        return
      }
      const page = this.pages[index]
      if (!page) {
        console.warn('[MultiTab] activeIndex watch: 页面不存在', index)
        return
      }
      
      // 避免循环触发：如果当前路由已经是目标路由，不进行跳转
      const currentPath = this.$route.path
      const currentFullPath = this.$route.fullPath
      const targetPath = page.path
      const targetFullPath = page.fullPath || page.path
      
      console.log('[MultiTab] activeIndex 变化:', {
        from: oldIndex,
        to: index,
        currentPath,
        targetPath,
        willNavigate: currentFullPath !== targetFullPath && currentPath !== targetPath
      })
      
      // 只有当路径不同时才进行跳转（避免在 onChange 中已经跳转的情况）
      if (currentFullPath !== targetFullPath && currentPath !== targetPath) {
        if (page.name) {
          this.$store.dispatch('tagsView/addCachedView', page)
        }
        // 使用 push 而不是 replace，保持历史记录
        this.$router.push({
          path: page.path,
          query: page.query,
          params: page.params,
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error('MultiTab: 路由跳转失败', err)
          }
        })
      } else {
        // 即使路径相同，也确保缓存视图被添加
        if (page.name) {
          this.$store.dispatch('tagsView/addCachedView', page)
        }
      }
    },
  },
  render() {
    const {
      onEdit,
      pagesList,
    } = this
    
    // 调试信息：输出所有访问过的页面
    console.log('[MultiTab] 🔍 render 被调用，pages 数量:', pagesList.length, '当前激活:', this.activeIndex)
    if (pagesList.length > 0) {
      console.log('[MultiTab] 📋 当前展示的页面列表:', pagesList.map((p, i) => ({
        index: i,
        path: p.path,
        title: p.meta?.customTitle || p.meta?.title || p.name,
        fullPath: p.fullPath,
        isActive: i === this.activeIndex
      })))
    } else {
      console.warn('[MultiTab] ⚠️ pages 数组为空，当前路由:', this.$route?.path)
    }
    
    // 循环遍历所有访问过的页面，确保每个菜单都被展示
    const panes = pagesList.map((page, index) => {
      // 确保页面对象完整
      const pageTitle = page.meta?.customTitle || page.meta?.title || page.name || page.path || '未命名页面'
      const pagePath = page.path || page.fullPath || ''
      // 使用索引作为 key，因为 Ant Design Vue 的 editable-card 类型需要数字 key
      const pageKey = String(index)
      
      return (
        <a-tab-pane
          style={{ height: 0 }}
          tab={this.renderTabPane(
            pageTitle,
            pagePath,
            index
          )}
          key={pageKey}
          closable={pagePath !== homeRoute.path}
        ></a-tab-pane>
      )
    })
    
    console.log('[MultiTab] panes 数组:', panes.length, 'activeIndex:', this.activeIndex)

    return (
      <div class="ant-pro-multi-tab">
        <div class="ant-pro-multi-tab-wrapper">
          <a-tabs
            hideAdd
            type={'editable-card'}
            activeKey={String(this.activeIndex)}
            onChange={(activeKey) => {
              const index = parseInt(activeKey, 10)
              console.log('[MultiTab] tabs onChange:', { 
                activeKey, 
                index, 
                '当前路由': this.$route.path,
                '目标页面': this.pages[index]?.path
              })
              // 点击tab时跳转到对应页面
              if (this.pages[index]) {
                const page = this.pages[index]
                console.log('[MultiTab] 准备跳转到:', {
                  path: page.path,
                  fullPath: page.fullPath,
                  query: page.query,
                  params: page.params
                })
                // 先更新 activeIndex，确保tab高亮
                this.activeIndex = index
                // 跳转到对应页面 - 即使路径相同也跳转，确保页面刷新
                this.$router.push({
                  path: page.path,
                  query: page.query || {},
                  params: page.params || {},
                }).then(() => {
                  console.log('[MultiTab] ✅ 路由跳转成功:', page.path)
                }).catch(err => {
                  if (err.name !== 'NavigationDuplicated') {
                    console.error('MultiTab: 路由跳转失败', err)
                  } else {
                    console.log('[MultiTab] 路由重复导航，已忽略')
                  }
                })
              } else {
                console.warn('[MultiTab] 页面不存在，索引:', index)
              }
            }}
            tabBarStyle={{
              background: '#FFF',
              margin: 0,
              paddingLeft: '16px',
              paddingTop: '1px',
            }}
            onEdit={(targetKey, action) => {
              console.log('[MultiTab] tabs onEdit:', { targetKey, action })
              onEdit(targetKey, action)
            }}
          >
            {panes}
          </a-tabs>
        </div>
      </div>
    )
  },
}
</script>

<style lang="less" scoped>
/* 激活tab文字颜色为蓝色 */
:deep(.ant-tabs-tab-active) {
  color: #165DFF !important;
  
  .ant-tabs-tab-btn {
    color: #165DFF !important;
  }
  
  span {
    color: #165DFF !important;
  }
  
  .ant-dropdown-trigger {
    color: #165DFF !important;
    
    span {
      color: #165DFF !important;
    }
  }
}

:deep(.ant-tabs-card .ant-tabs-tab-active),
:deep(.ant-tabs-editable-card .ant-tabs-tab-active) {
  color: #165DFF !important;
  
  .ant-tabs-tab-btn {
    color: #165DFF !important;
  }
  
  span {
    color: #165DFF !important;
  }
  
  .ant-dropdown-trigger {
    color: #165DFF !important;
    
    span {
      color: #165DFF !important;
    }
  }
  
  * {
    color: #165DFF !important;
  }
}
</style>

<style>
/* 全局样式 - 确保激活tab文字颜色为蓝色 */
.ant-pro-multi-tab .ant-tabs-tab.ant-tabs-tab-active,
.ant-pro-multi-tab .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn,
.ant-pro-multi-tab .ant-tabs-tab.ant-tabs-tab-active span,
.ant-pro-multi-tab .ant-tabs-tab.ant-tabs-tab-active .ant-dropdown-trigger,
.ant-pro-multi-tab .ant-tabs-tab.ant-tabs-tab-active .ant-dropdown-trigger span,
.ant-pro-multi-tab .ant-tabs-tab.ant-tabs-tab-active * {
  color: #165DFF !important;
}

.ant-pro-multi-tab-wrapper .ant-tabs-tab.ant-tabs-tab-active,
.ant-pro-multi-tab-wrapper .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn,
.ant-pro-multi-tab-wrapper .ant-tabs-tab.ant-tabs-tab-active span,
.ant-pro-multi-tab-wrapper .ant-tabs-tab.ant-tabs-tab-active .ant-dropdown-trigger,
.ant-pro-multi-tab-wrapper .ant-tabs-tab.ant-tabs-tab-active .ant-dropdown-trigger span,
.ant-pro-multi-tab-wrapper .ant-tabs-tab.ant-tabs-tab-active * {
  color: #165DFF !important;
}
</style>
